/**
 * Live Preview pane for PageContentManager.
 * Shows the public-facing page in an iframe and refreshes on Save.
 *
 * Props:
 *   - pageConfig: PAGE_CONFIG entry for the active page (may include .previewUrl
 *     for static routes OR .previewUrlSample for detail-template pages that
 *     need a sample slug fetched from a list endpoint)
 *   - refreshKey: a number/string that bumps on Save → triggers iframe reload
 *   - viewport: 'desktop' | 'mobile'
 *   - onViewportChange: (v) => void
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Monitor, Smartphone, ExternalLink, RefreshCw, Loader2 } from 'lucide-react';

const API_URL = window.location.origin;

export const LivePreview = ({ pageConfig, refreshKey, viewport = 'desktop', onViewportChange }) => {
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);

  // Resolve preview URL — static or fetch-a-sample-slug for detail templates
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      setError(null);
      setIframeLoading(true);
      if (pageConfig?.previewUrl) {
        setResolvedUrl(pageConfig.previewUrl);
        return;
      }
      const sample = pageConfig?.previewUrlSample;
      if (!sample) {
        setError('No preview URL configured');
        setResolvedUrl(null);
        return;
      }
      setResolving(true);
      try {
        const res = await fetch(`${API_URL}${sample.endpoint}`);
        if (!res.ok) throw new Error(`Failed to fetch sample: ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data) ? data : (data.items || data.universities || data.events || data.posts || data.programs || []);
        const first = items?.[0];
        if (!first) {
          if (!cancelled) {
            setError('No published items to preview — publish at least one to see live preview');
            setResolvedUrl(null);
          }
          return;
        }
        const slug = first[sample.slugKey];
        if (!slug) {
          if (!cancelled) {
            setError(`First item has no '${sample.slugKey}' field`);
            setResolvedUrl(null);
          }
          return;
        }
        if (!cancelled) setResolvedUrl(`${sample.pathPrefix}${slug}`);
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setResolvedUrl(null);
        }
      } finally {
        if (!cancelled) setResolving(false);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [pageConfig]);

  const iframeSrc = resolvedUrl
    ? `${resolvedUrl}${resolvedUrl.includes('?') ? '&' : '?'}_cmsPreview=${refreshKey}`
    : null;

  const widthClass = viewport === 'mobile' ? 'max-w-[420px]' : 'w-full';

  return (
    <div className="flex flex-col h-full" data-testid="live-preview-pane">
      {/* Preview Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-ux-charcoal/70">Live Preview</span>
          {resolvedUrl && (
            <code className="text-xs text-ux-charcoal/50 truncate max-w-[200px]" title={resolvedUrl}>
              {resolvedUrl}
            </code>
          )}
        </div>
        <div className="flex items-center gap-1">
          <div className="flex items-center bg-white rounded-md border p-0.5">
            <button
              onClick={() => onViewportChange?.('desktop')}
              className={`p-1.5 rounded ${viewport === 'desktop' ? 'bg-ux-cyan text-white' : 'text-ux-charcoal/60 hover:bg-gray-100'}`}
              data-testid="preview-viewport-desktop"
              title="Desktop"
            >
              <Monitor className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onViewportChange?.('mobile')}
              className={`p-1.5 rounded ${viewport === 'mobile' ? 'bg-ux-cyan text-white' : 'text-ux-charcoal/60 hover:bg-gray-100'}`}
              data-testid="preview-viewport-mobile"
              title="Mobile"
            >
              <Smartphone className="w-3.5 h-3.5" />
            </button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            asChild
          >
            <a href={resolvedUrl || '#'} target="_blank" rel="noopener noreferrer" title="Open in new tab">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </Button>
        </div>
      </div>

      {/* Iframe Container */}
      <div className="flex-1 bg-gray-100 rounded-b-lg overflow-hidden relative flex items-start justify-center">
        {resolving ? (
          <div className="flex flex-col items-center justify-center h-full text-ux-charcoal/60">
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <p className="text-sm">Resolving preview URL...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <p className="text-sm text-ux-charcoal/60 mb-2">{error}</p>
            <p className="text-xs text-ux-charcoal/40">Edits will still save normally.</p>
          </div>
        ) : iframeSrc ? (
          <div className={`${widthClass} h-full bg-white shadow-sm relative`}>
            {iframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 pointer-events-none">
                <RefreshCw className="w-6 h-6 animate-spin text-ux-cyan" />
              </div>
            )}
            <iframe
              key={refreshKey}
              src={iframeSrc}
              title="Live Preview"
              className="w-full h-full border-0"
              data-testid="live-preview-iframe"
              onLoad={() => setIframeLoading(false)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LivePreview;
